import { State } from './state';
import { LoadScreen } from '../helpers/loadscreen';

/**
 * A class for managing game states and the transitions between them.
 */
class _StateManager {
    _states: any;
    _activeState: any;

    constructor() {
        this._states = {};
        this._activeState = null;
    }

    /**
     * Registers a new state.
     *
     * @param name
     * The name of this state.
     * @param classFunc
     * The class that runs this state.
     */
    register(name: any, classFunc: any) {
  if (this._states[name]) {
    console.warn('Two states registered with the same name!');
    return;
  }

  for (let i in this._states) {
    if (this._states.hasOwnProperty(i)) {
      if (this._states[i] instanceof classFunc) {
        throw new Error('Two states registered with the same type!');
      }
    }
  }

  const stateInstance = new classFunc();
  if (!(stateInstance instanceof State)) {
    console.warn('Registered state does not inherit from base state.');
  }

  stateInstance.__isPrepared = false;
  stateInstance.__isPrepareOnced = false;
  this._states[name] = stateInstance;
    }

    /**
     * Retrieves a state by name.
     *
     * @param name
     * @returns {*}
     */
    get(name: any) {
  if (!this._states[name]) {
    throw new Error('Attempted to retrieve unregistered state.');
  }
  return this._states[name];
    }

    async prepare(name: any): Promise<void> {
  const state = this._states[name];
  if (!state) {
    throw new Error('Attempted to prepare an unregistered state.');
  }

  if (!state.__isPrepareOnced) {
    await state.prepareOnce();
    state.__isPrepareOnced = true;
  }

  if (!state.__isPrepared) {
    await state.prepare();
    state.__isPrepared = true;
  }
    }

    switch(name: any) {
  const state = this._states[name];
  if (!state) {
    throw new Error('Attempted to switch to an unregistered state.');
  }

  if (!state.__isPrepared) {
    throw new Error('Attempted to switch to an unprepared state.');
  }

  if (this._activeState) {
    this._activeState.leave();
    this._activeState.__isPrepared = false;
  }

  this._activeState = state;
  this._activeState.enter();
    }

    async prepareAndSwitch(name: any): Promise<void> {
  // Show loading indicator
  LoadScreen.show();
  LoadScreen.setMessage("Loading...");
  LoadScreen.setProgress(0);

  try {
    // Wait for prepare to complete (this will handle its own loading screen if needed)
    await this.prepare(name);

    // Switch to the state
    this.switch(name);
  } finally {
    // Ensure loading screen is hidden
    LoadScreen.hide();
  }
    }

    update(delta: any) {
  if (this._activeState) {
    this._activeState.update(delta);
  }
    }
}

/**
 * @global
 * @type {_StateManager}
 */
export const StateManager = new _StateManager();
